import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/constants';

/**
 * Gmail-Style Email Inbox for TagQuest
 * 
 * Features:
 * - Automation dashboard at top
 * - Sidebar navigation (Inbox, Sent, Scheduled, Automation, etc.)
 * - Threaded conversations
 * - Full email preview modal
 * - Scheduler status and controls
 * - Link to legacy email page for full functionality
 */

const GmailStyleInbox = () => {
  const [currentView, setCurrentView] = useState('inbox'); // inbox, sent, scheduled, automation
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Automation state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [emailJobsStatus, setEmailJobsStatus] = useState(null);
  const [pendingLeads, setPendingLeads] = useState({ count: 0, leads: [] });
  const [communicationLog, setCommunicationLog] = useState({ log: [], stats: {} });

  const backendUrl = BACKEND_URL;

  useEffect(() => {
    loadEmails();
    loadSchedulerStatus();
    loadPendingLeads();
    loadCommunicationLog();
  }, [currentView]);

  // Load scheduler and automation status
  const loadSchedulerStatus = async () => {
    try {
      const [schedulerRes, jobsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/scheduler-status`, { headers: { 'X-Local-Dev': 'true' } }),
        fetch(`${backendUrl}/api/admin/email-jobs-status`, { headers: { 'X-Local-Dev': 'true' } })
      ]);
      const schedulerData = await schedulerRes.json();
      const jobsData = await jobsRes.json();
      setSchedulerStatus(schedulerData);
      setEmailJobsStatus(jobsData);
    } catch (err) {
      console.error('Failed to load scheduler status:', err);
    }
  };

  // Load pending leads for automation dashboard
  const loadPendingLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/preview-daily-leads?hours_back=24`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success) {
        setPendingLeads({ count: data.pending_count || 0, leads: data.leads || [] });
      }
    } catch (err) {
      console.error('Failed to load pending leads:', err);
    }
  };

  // Load communication log for tracking
  const loadCommunicationLog = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/communication-log?days=30`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success) {
        setCommunicationLog({ log: data.log || [], stats: data.stats || {} });
      }
    } catch (err) {
      console.error('Failed to load communication log:', err);
    }
  };

  // Toggle an email job
  const toggleEmailJob = async (jobId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${backendUrl}/api/admin/email-jobs/toggle`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ job_id: jobId, enabled })
      });
      loadSchedulerStatus();
    } catch (err) {
      console.error('Failed to toggle job:', err);
    }
  };

  // Trigger manual digest send
  const triggerManualDigest = async (type) => {
    try {
      const endpoint = type === 'daily' 
        ? '/api/admin/trigger-daily-digest'
        : '/api/admin/trigger-hot-lead-digest';
      const token = localStorage.getItem('token');
      await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      alert(`${type === 'daily' ? 'Daily' : 'Hot'} lead digest triggered!`);
      loadSchedulerStatus();
      loadPendingLeads();
    } catch (err) {
      console.error('Failed to trigger digest:', err);
    }
  };

  const loadEmails = async () => {
    setLoading(true);
    try {
      if (currentView === 'inbox') {
        const token = localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/admin/inbound-emails?limit=100`, {
          headers: { 
            'X-Local-Dev': 'true',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const data = await response.json();
        if (data?.success) {
          // Group emails into threads by subject/inspection
          const threaded = groupIntoThreads(data.emails || []);
          setEmails(threaded);
        }
      } else if (currentView === 'sent') {
        // Load sent emails from email history
        const token = localStorage.getItem('token');
        const response = await fetch(`${backendUrl}/api/admin/email-history?limit=100`, {
          headers: { 
            'X-Local-Dev': 'true',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const data = await response.json();
        if (data?.success) {
          setSentEmails(data.history || []);
        }
      }
    } catch (err) {
      console.error('Failed to load emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupIntoThreads = (emailList) => {
    // Group by inspection_id or subject line
    const threads = {};
    
    emailList.forEach(email => {
      const threadKey = email.inspection_id || email.subject || 'unknown';
      if (!threads[threadKey]) {
        threads[threadKey] = {
          id: threadKey,
          subject: email.subject || 'No subject',
          emails: [],
          lastDate: email.received_at,
          from: email.from,
          unread: email.status === 'pending',
          inspectionId: email.inspection_id
        };
      }
      threads[threadKey].emails.push(email);
      // Update last date if this email is newer
      if (new Date(email.received_at) > new Date(threads[threadKey].lastDate)) {
        threads[threadKey].lastDate = email.received_at;
      }
    });

    // Convert to array and sort by last date
    return Object.values(threads).sort((a, b) => 
      new Date(b.lastDate) - new Date(a.lastDate)
    );
  };

  const openThread = (thread) => {
    setSelectedThread(thread);
  };

  const closeThread = () => {
    setSelectedThread(null);
  };

  const openPreviewModal = (email) => {
    setPreviewEmail(email);
    setShowPreviewModal(true);
  };

  const filteredEmails = emails.filter(thread => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      thread.subject?.toLowerCase().includes(query) ||
      thread.from?.toLowerCase().includes(query) ||
      thread.emails.some(e => e.text?.toLowerCase().includes(query))
    );
  });

  // Format next run time nicely
  const formatNextRun = (isoString) => {
    if (!isoString) return 'Not scheduled';
    const date = new Date(isoString);
    const now = new Date();
    const diff = date - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
    if (hours > 0) return `in ${hours}h ${mins}m`;
    if (mins > 0) return `in ${mins}m`;
    return 'Soon';
  };

  const sidebarItems = [
    { id: 'inbox', label: '📥 Inbox', count: emails.length },
    { id: 'sent', label: '📤 Sent', count: sentEmails.length },
    { id: 'scheduled', label: '📅 Scheduled', count: schedulerStatus?.jobs?.length || 0, divider: true },
    { id: 'automation', label: '🤖 Automation', count: pendingLeads.count, highlight: pendingLeads.count > 0 },
    { id: 'drafts', label: '📝 Drafts', count: 0 },
    { id: 'trash', label: '🗑️ Trash', count: 0 },
  ];

  // Render automation dashboard content
  const renderAutomationDashboard = () => (
    <div className="p-6 space-y-6 overflow-y-auto h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">🤖 Email Automation Dashboard</h2>
        <button 
          onClick={loadSchedulerStatus}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Scheduler Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Overall Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-3 h-3 rounded-full ${schedulerStatus?.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="font-semibold text-gray-700">Scheduler</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}
          </div>
          <div className="text-xs text-gray-500 mt-1">{schedulerStatus?.job_count || 0} jobs configured</div>
        </div>

        {/* Daily Digest */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">📬</span>
            <span className="font-semibold text-gray-700">Daily Digest</span>
          </div>
          <div className="text-sm text-gray-600">
            Next: {formatNextRun(schedulerStatus?.jobs?.find(j => j.id === 'daily_digest')?.next_run_utc)}
          </div>
          <button 
            onClick={() => triggerManualDigest('daily')}
            className="mt-2 px-3 py-1 bg-[#0DB14B] hover:bg-[#0DB14B]/90 text-white text-xs rounded-lg"
          >
            Send Now
          </button>
        </div>

        {/* Hot Leads */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">🔥</span>
            <span className="font-semibold text-gray-700">Hot Leads</span>
          </div>
          <div className="text-sm text-gray-600">
            Next: {formatNextRun(schedulerStatus?.jobs?.find(j => j.id === 'hot_lead_digest')?.next_run_utc)}
          </div>
          <button 
            onClick={() => triggerManualDigest('hot')}
            className="mt-2 px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs rounded-lg"
          >
            Send Now
          </button>
        </div>

        {/* Pending Leads */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">📊</span>
            <span className="font-semibold text-gray-700">Pending Leads</span>
          </div>
          <div className="text-3xl font-bold text-[#0DB14B]">{pendingLeads.count}</div>
          <div className="text-xs text-gray-500 mt-1">Ready for next digest</div>
        </div>
      </div>

      {/* Job Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-bold text-gray-900 mb-4">📅 Scheduled Email Jobs</h3>
        <div className="space-y-3">
          {emailJobsStatus?.jobs?.map(job => (
            <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${job.paused ? 'bg-gray-400' : 'bg-green-500'}`}></div>
                <div>
                  <div className="font-medium text-gray-800">{job.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                  <div className="text-xs text-gray-500">
                    {job.paused ? 'Paused' : `Next: ${formatNextRun(job.next_run_utc)}`}
                  </div>
                </div>
              </div>
              <button
                onClick={() => toggleEmailJob(job.id, job.paused)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  job.paused 
                    ? 'bg-[#0DB14B] hover:bg-[#0DB14B]/90 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {job.paused ? '▶️ Enable' : '⏸️ Pause'}
              </button>
            </div>
          ))}
          {(!emailJobsStatus?.jobs || emailJobsStatus.jobs.length === 0) && (
            <div className="text-gray-500 text-center py-4">No scheduled jobs found</div>
          )}
        </div>
      </div>

      {/* Weekly Reports Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
        <h3 className="font-bold text-gray-900 mb-3">📈 Weekly Reports &amp; Summaries</h3>
        <p className="text-sm text-gray-600 mb-4">
          Weekly performance summaries are sent to Regional Vice Presidents and Regional Directors 
          every Monday at 8:00 AM CST. Reports include conversion rates, top performers, and lead pipeline status.
        </p>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm">
            📋 Preview Weekly Report
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm">
            ⚙️ Configure Recipients
          </button>
        </div>
      </div>

      {/* 🧠 Smart Communication Tracking */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">🧠 Smart Communication Tracking</h3>
          <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            Last 30 days
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{communicationLog.stats?.total_sends || 0}</div>
            <div className="text-xs text-gray-500">Batch Sends</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{communicationLog.stats?.total_recipients || 0}</div>
            <div className="text-xs text-gray-500">Total Recipients</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {Object.keys(communicationLog.stats?.by_type || {}).length}
            </div>
            <div className="text-xs text-gray-500">Email Types</div>
          </div>
        </div>
        
        {/* Recent Sends */}
        {communicationLog.log?.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {communicationLog.log.slice(0, 5).map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {entry.type === 'daily_digest' ? '📬' : 
                     entry.type === 'hot_lead_digest' ? '🔥' : 
                     entry.type === 'weekly_summary' ? '📈' : '📧'}
                  </span>
                  <div>
                    <div className="font-medium text-gray-800">
                      {entry.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry.recipient_count} recipients
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(entry.sent_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4 text-sm">
            📊 No email batches sent yet. Communication will be tracked here.
          </div>
        )}
        
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <strong>✅ Duplicate Prevention Active:</strong> The system automatically prevents 
          sending the same email type within cooldown periods (e.g., 6 days for weekly summaries).
        </div>
      </div>

      {/* Link to Legacy Page */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <div className="font-medium text-yellow-800">🔧 Need Advanced Features?</div>
          <div className="text-sm text-yellow-700">
            Regional officers, template previews, and manual triggers are in the original email management page.
          </div>
        </div>
        <Link 
          to="/admin/emails"
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm whitespace-nowrap"
        >
          Open Legacy Page →
        </Link>
      </div>
    </div>
  );

  // Render scheduled emails view
  const renderScheduledView = () => (
    <div className="p-6 overflow-y-auto h-full">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">📅 Scheduled Emails</h2>
      <div className="space-y-4">
        {schedulerStatus?.jobs?.map((job, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{job.name || job.id}</div>
                <div className="text-sm text-gray-500 mt-1">{job.trigger}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-[#0DB14B]">{formatNextRun(job.next_run_utc)}</div>
                <div className="text-xs text-gray-400">Next run</div>
              </div>
            </div>
          </div>
        ))}
        {(!schedulerStatus?.jobs || schedulerStatus.jobs.length === 0) && (
          <div className="text-center text-gray-500 py-12">
            <span className="text-4xl block mb-2">📭</span>
            No scheduled emails
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      <div className="w-64 bg-[#00314F] border-r border-white/10 flex flex-col">
        <div className="p-4">
          <button className="w-full bg-[#0DB14B] hover:bg-[#0DB14B]/90 text-white rounded-full py-3 px-6 font-semibold shadow-md flex items-center justify-center gap-2 transition-all">
            <span className="text-xl">✉️</span>
            Compose
          </button>
        </div>

        <nav className="flex-1 px-2 overflow-y-auto">
          {sidebarItems.map((item, idx) => (
            <React.Fragment key={item.id}>
              {item.divider && <div className="border-t border-white/10 my-2 mx-2" />}
              <button
                onClick={() => setCurrentView(item.id)}
                className={`w-full text-left px-4 py-3 rounded-lg mb-1 flex items-center justify-between transition-all ${
                  currentView === item.id
                    ? 'bg-[#0DB14B]/20 text-white font-semibold'
                    : 'hover:bg-white/10 text-white/80'
                }`}
              >
                <span>{item.label}</span>
                {item.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    item.highlight 
                      ? 'bg-[#0DB14B] text-white' 
                      : 'bg-white/20 text-white'
                  }`}>
                    {item.count}
                  </span>
                )}
              </button>
            </React.Fragment>
          ))}
        </nav>

        {/* Footer with links */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link 
            to="/admin/emails"
            className="block text-xs text-white/60 hover:text-white/90 transition-colors"
          >
            📋 Legacy Email Manager
          </Link>
          <Link 
            to="/admin/regional-officers"
            className="block text-xs text-white/60 hover:text-white/90 transition-colors"
          >
            👥 Regional Officers
          </Link>
          <p className="text-xs text-white/40 mt-2">Powered by SendGrid</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Search Bar */}
        <div className="bg-white border-b border-gray-300 p-4">
          <div className="relative max-w-2xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Search emails..."
              className="w-full px-4 py-3 pl-12 bg-[#F1F3F4] rounded-full focus:outline-none focus:bg-white focus:shadow-md border border-transparent focus:border-[#0DB14B] transition-all"
            />
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl">
              🔍
            </span>
          </div>
        </div>

        {/* Email List or Thread View or Automation Dashboard */}
        <div className="flex-1 overflow-hidden">
          {/* Show automation dashboard */}
          {currentView === 'automation' ? renderAutomationDashboard() : null}
          
          {/* Show scheduled view */}
          {currentView === 'scheduled' ? renderScheduledView() : null}
          
          {/* Show email views */}
          {(currentView === 'inbox' || currentView === 'sent' || currentView === 'drafts' || currentView === 'trash') && !selectedThread ? (
            /* Email Thread List */
            <div className="h-full overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  Loading emails...
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <span className="text-6xl mb-4">📭</span>
                  <p className="text-lg">No emails in {currentView}</p>
                </div>
              ) : (
                filteredEmails.map(thread => (
                  <div
                    key={thread.id}
                    onClick={() => openThread(thread)}
                    className={`border-b border-gray-200 p-4 cursor-pointer hover:shadow-md transition-all ${
                      thread.unread ? 'bg-white font-semibold' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={thread.unread ? 'text-gray-900' : 'text-gray-600'}>
                            {thread.from || 'Unknown sender'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {thread.emails.length > 1 && `(${thread.emails.length})`}
                          </span>
                        </div>
                        <div className={`truncate ${thread.unread ? 'text-gray-900' : 'text-gray-700'}`}>
                          {thread.subject}
                        </div>
                        <div className="text-sm text-gray-500 truncate mt-1">
                          {thread.emails[thread.emails.length - 1]?.text?.substring(0, 100)}...
                        </div>
                      </div>
                      <div className="ml-4 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(thread.lastDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}
          
          {/* Thread/Conversation View */}
          {selectedThread && (currentView === 'inbox' || currentView === 'sent') ? (
            <div className="h-full flex flex-col bg-white">
              {/* Thread Header */}
              <div className="border-b border-gray-300 p-4 flex items-center justify-between">
                <button
                  onClick={closeThread}
                  className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                >
                  <span className="text-xl">←</span>
                  Back to {currentView}
                </button>
                <div className="flex gap-2">
                  <button className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    🗑️ Delete
                  </button>
                  <button className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                    📥 Archive
                  </button>
                </div>
              </div>

              {/* Thread Subject */}
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {selectedThread.subject}
                </h2>
                {selectedThread.inspectionId && (
                  <p className="text-sm text-gray-500 mt-2">
                    Related to inspection: {selectedThread.inspectionId}
                  </p>
                )}
              </div>

              {/* Email Chain */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {selectedThread.emails.map((email, index) => (
                  <div
                    key={email.id || index}
                    className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden"
                  >
                    {/* Email Header */}
                    <div className="bg-gray-50 p-4 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {email.from || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            to: TagQuest@pyebarkerfs.com
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(email.received_at).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="p-4">
                      <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                        {email.text || email.html || 'No content'}
                      </div>

                      {/* AI Analysis Badge */}
                      {email.ai_analysis && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="font-semibold text-blue-900 text-sm mb-2">
                            🤖 AI Analysis
                          </div>
                          <div className="text-sm text-blue-800">
                            <strong>Recommendation:</strong> {email.ai_analysis.recommended_action || 'None'}
                          </div>
                          {email.ai_analysis.extracted_facts && (
                            <div className="mt-2 text-xs text-blue-700">
                              {JSON.stringify(email.ai_analysis.extracted_facts, null, 2)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => openPreviewModal(email)}
                          className="px-4 py-2 bg-[#0DB14B] text-white rounded-lg hover:bg-[#0DB14B]/90"
                        >
                          👁️ Preview Full Email
                        </button>
                        <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                          💬 Reply
                        </button>
                        <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                          ↪️ Forward
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              <div className="border-t border-gray-300 p-4 bg-gray-50">
                <div className="flex gap-2">
                  <textarea
                    placeholder="Reply to this thread..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#0DB14B] focus:ring-2 focus:ring-[#0DB14B]/20"
                    rows="3"
                  />
                  <button className="px-6 bg-[#0DB14B] text-white rounded-lg font-semibold hover:bg-[#0DB14B]/90">
                    Send
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Full Email Preview Modal */}
      {showPreviewModal && previewEmail && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-[#00314F] px-6 py-4 flex items-center justify-between border-b border-white/10">
              <div className="text-white font-bold text-lg">
                📧 Full Email Preview - As Recipient Sees It
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-white/80 hover:text-white text-3xl leading-none px-2"
                title="Close"
              >
                ×
              </button>
            </div>

            {/* Email Preview (Realistic Gmail-style) */}
            <div className="bg-white overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              {/* Gmail-style Email Header */}
              <div className="bg-white p-6 border-b border-gray-200">
                <h1 className="text-2xl font-semibold text-gray-900 mb-4">
                  {previewEmail.subject || 'No Subject'}
                </h1>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0DB14B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {previewEmail.from?.charAt(0)?.toUpperCase() || 'T'}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {previewEmail.from || 'TagQuest@pyebarkerfs.com'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      to: me
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(previewEmail.received_at).toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="p-6">
                <div className="prose prose-lg max-w-none">
                  {previewEmail.html ? (
                    <iframe
                      srcDoc={previewEmail.html}
                      className="w-full border-0"
                      style={{ minHeight: '400px' }}
                      title="Email HTML Content"
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {previewEmail.text || 'No content available'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Close
              </button>
              <button className="px-6 py-2 bg-[#0DB14B] text-white rounded-lg hover:bg-[#0DB14B]/90 font-semibold">
                💬 Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmailStyleInbox;

